<template>
  <div id="roadmapComponent">

    <!-- ROADMAP -->
    <div v-if="roadmapGroups">
      <div class="roadmap-header d-lg-flex">
        <h2>Roadmap<span> October 2023</span></h2>
        <div v-if="roadmapLegend" class="legend">
          <ul class="d-lg-flex">
            <li v-for="(item, index) in roadmapLegend"
                :key="'roadmapLegend' + index">
              <span class="circle"
                    :style="{backgroundColor: item.colour}" />
              <span>{{ item.title }}</span>
            </li>
          </ul>
        </div>
      </div>
      <div class="content">
        <div v-for="(item, index) in roadmapGroups"
             class="spacing"
             :key="'roadmapGroup' + index">
            <div class="d-flex mb-3 mt-2 align-items-center">
            <h3>{{ item.title }}</h3>
            <div class="roadmap-tooltip">
              <img @mouseover="toggleTooltip(index)"
                   @mouseleave="toggleTooltip(index)"
                   :src="tooltipIcon(index)"
                   alt="info"
                   class="ml-2 mb-1">
              <span :class="{open: tooltipIsActive(index)}">
                {{ item.tooltip }}
              </span>
            </div>
          </div>
          <div v-for="(tile, tileIndex) in item.tiles"
               :key="'roadmapGroup' + index + 'Tile' + tileIndex"
               class="tile">
            <h4 :class="{'m-0 no-points': !tile.points}"
                :style="{backgroundColor: tile.colour}">
              {{ tile.title }}<br>
              <span class="d-lg-none pretext">{{ tile.pretext }}</span>
            </h4>
            <ul v-if="tile.points">
              <li v-for="(point, pointIndex) in tile.points"
                  :key="'roadmapGroup' + index + 'Tile' + tileIndex + 'Point' + pointIndex">{{ point }}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- ONT CHANGES -->
    <div v-if="roadmapOntChanges">
      <div class="content">
        <div v-for="(change, index) in roadmapOntChanges"
             :class="{'spacing': change.title}"
             :key="'roadmapOntChange' + index">
          <div v-if="change.title" class="d-flex mb-3 mt-2 align-items-center d-md-none">
            <h3>{{ change.title }}</h3>
            <div class="roadmap-tooltip">
              <img @mouseover="toggleTooltip(index)"
                   @mouseleave="toggleTooltip(index)"
                   :src="tooltipIcon(index)"
                   alt="info"
                   class="ml-2 mb-1">
              <span :class="{open: tooltipIsActive(index)}">
                {{ change.tooltip }}
              </span>
            </div>
          </div>
          <div v-for="(tile, tileIndex) in change.tiles"
               :key="'roadmapGroup' + index + 'Tile' + tileIndex"
               class="tile">
            <h4 :class="{'m-0 no-points': !tile.points}"
                :style="{backgroundColor: tile.colour}">
              {{ tile.title }}<br>
              <span class="d-lg-none pretext">{{ tile.pretext }}</span>
            </h4>
            <div class="ont-subheading"><p>ONT Changes</p></div>
            <ul v-if="tile.points">
              <li v-for="(point, pointIndex) in tile.points"
                  :key="'roadmapGroup' + index + 'Tile' + tileIndex + 'Point' + pointIndex">{{ point }}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- CALENDAR -->
    <div class="d-lg-none mobile-calendar-image"
         :class="{open: mobileCalendarImageIsOpen}">
      <img src="/themes/custom/chorus/images/global/close-white.svg"
           alt="close"
           @click="toggleMobileCalendarImage"
           class="close-mobile-calendar m-3"
           :class="{'d-none': !mobileCalendarImageIsOpen}">
      <img :src="mobileCalendarImage" alt="calendar"
           @click="toggleMobileCalendarImage">
    </div>
    <div class="d-none d-lg-block mt-5 pt-5 calendar">
      <div class="roadmap-header d-lg-flex">
        <h2>
          {{ calendarTitle }}
          <span v-if="calendarSubtitle">{{ calendarSubtitle }}</span>
        </h2>
        <div v-if="calendarLegend" class="legend">
          <ul class="d-lg-flex">
            <li v-for="(item, index) in calendarLegend"
                :key="'calendarLegend' + index">
            <span class="circle"
                  :style="{backgroundColor: item.colour}" />
              <span>{{ item.title }}</span>
            </li>
          </ul>
        </div>
        <div v-if="yearToggles" class="year-toggles">
          <img src="/themes/custom/chorus/images/global/purple-left-arrow.svg"
               alt="arrow"
               :class="{'enabled': canGoToYear(activeYear - 1)}"
               @click="changeYear(activeYear - 1)">
          <span v-for="(item, index) in yearToggles"
              :key="'yearToggle' + index"
                class="px-2"
              :class="{'d-none': item != activeYear}">{{ activeYear }}
          </span>
          <img src="/themes/custom/chorus/images/global/purple-right-arrow.svg"
               alt="arrow"
               :class="{'enabled': canGoToYear(activeYear + 1)}"
               @click="changeYear(activeYear + 1)">
        </div>
      </div>
      <div class="d-lg-flex justify-content-start mt-4">
        <div class="year-label first-col pr-1" :class="{'wider': !roadmapGroups}">{{ yearRange }}</div>
        <div class="d-lg-flex justify-content-around w-100">
          <div v-for="(month, index) in calendarMonths"
               :key="'month' + index"
               class="month-label">
            {{ month }}
          </div>
        </div>
      </div>
      <div v-if="calendarGroupsData">
        <div v-for="(group, index) in calendarGroupsData"
             :key="'calendarGroup' + index"
             :style="{minHeight: calendarRowMinHeight(group.rows.length) }"
             :class="{'custom-style': calendarGroups2}"
             class="d-lg-flex w-100">
          <div class="calendar-group-name first-col"
               :class="[{'wider': !roadmapGroups, 'mb-1': !calendarGroups2}, themeClass(group)]">
            {{ group.title }}
            <div v-if="group.tooltip" class="roadmap-tooltip">
              <img @mouseover="toggleTooltip(index)"
                   @mouseleave="toggleTooltip(index)"
                   :src="tooltipIcon(index)"
                   alt="info"
                   class="ml-2 mb-1">
              <span :class="{open: tooltipIsActive(index)}">
                {{ group.tooltip }}
              </span>
            </div>
          </div>
          <div class="calendar-tile-wrapper p-1" :class="themeClass(group)">
            <div v-for="(row, rowIndex) in group.rows"
                 :key="'calendarGroup' + index + 'row' + rowIndex"
                 class="d-lg-flex">
              <div v-for="(tile, tileIndex) in row"
                   :key="'calendarGroup' + index + 'row' + rowIndex + 'tile' + tileIndex"
                   class="calendar-tile"
                   :class="{'has-tooltip': tile.tooltip, 'outline-style': tile.outlineStyle}"
                   :style="calendarTileStyles(tile, tileIndex, row.length)">
                <p v-if="tile.tooltip" class="tile-tooltip">{{ tile.tooltip }}</p>
                <p class="m-0 pr-0">{{ tile.title }}<span v-if="tile.blurb" class="blurb"> - {{ tile.blurb }}</span></p>
                <span v-if="tile.hasArrow" class="arrow"></span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script type="ts">
import he from 'he'

export default {
  name: 'RoadmapComponent',
  data: function() {
    return {
      roadmapLegend: window.roadmapLegend ? JSON.parse(he.decode(window.roadmapLegend)) : '',
      roadmapGroups: window.roadmapGroups ? JSON.parse(he.decode(window.roadmapGroups)) : '',
      roadmapOntChanges: window.roadmapOntChanges ? JSON.parse(he.decode(window.roadmapOntChanges)) : '',
      mobileCalendarImage: window.mobileCalendarImage,
      yearRange: window.yearRange,
      calendarTitle: window.calendarTitle ? window.calendarTitle : 'Calendar',
      calendarSubtitle: window.calendarSubtitle ? window.calendarSubtitle : '',
      calendarMonths: window.calendarMonths ? JSON.parse(he.decode(window.calendarMonths)) : '',
      calendarLegend: window.calendarLegend ? JSON.parse(he.decode(window.calendarLegend)) : '',
      calendarGroups: window.calendarGroups ? JSON.parse(he.decode(window.calendarGroups)) : '',
      calendarGroups2: window.calendarGroups2 ? JSON.parse(he.decode(window.calendarGroups2)) : '',
      yearToggles: window.yearToggles ? JSON.parse(he.decode(window.yearToggles)) : '',
      activeYear: new Date().getFullYear(),
      mobileCalendarImageIsOpen: false,
      activeTooltips: [],
    }
  },
  computed: {
    calendarGroupsData () {
      if (this.calendarGroups2) {
        if (this.activeYear === new Date().getFullYear()) {
          return this.calendarGroups
        } else {
          return this.calendarGroups2
        }
      }
      return this.calendarGroups
    }
  },
  methods: {
    themeClass (group) {
      return group.theme ? group.theme : ''
    },
    canGoToYear (year) {
      return this.yearToggles.find((element) => {
        return element == year
      })
    },
    changeYear(year) {
      if (this.canGoToYear(year)) {
        this.activeYear = year
      }
    },
    toggleMobileCalendarImage() {
      if (this.mobileCalendarImageIsOpen) {
        this.mobileCalendarImageIsOpen = false
        document.body.classList.remove('modal-open')
      } else {
        this.mobileCalendarImageIsOpen = true
        document.body.classList.add('modal-open')
      }
    },
    calendarTileStyles(tile, tileIndex, tileCount) {
      let iteration = tileIndex + 1
      let offset = 0
      if (tileCount > 1 && iteration === tileCount) {
        offset = .3
      }

      let monthCount = this.calendarMonths.length
      let tileStart = tile.start
      let tileEnd = tile.end

      let leftPosition = (tileStart / monthCount * 101) + offset + '%'
      let difference = tileEnd - tileStart
      let width = (difference / monthCount * 100) + '%'
      let bgColor = '#FFFFFF'

      // All tiles have white bg, apart from milestones
      if (tile.isMilestone) {
        bgColor = '#F7E2EC'
      }

      return {
        width: width,
        color: tile.colour,
        border: '1px solid ' + tile.colour,
        borderLeftWidth: '6px',
        backgroundColor: bgColor,
        left: leftPosition
      }
    },
    calendarRowMinHeight(rowCount) {
      let extraSpace = 45

      if (rowCount < 3) {
        extraSpace = 12
      } else if (rowCount < 5) {
        extraSpace = 20
      }

      return (rowCount * 45 + extraSpace) + 'px'
    },
    tooltipIcon(tooltip) {
      if (this.tooltipIsActive(tooltip)) {
        return '/themes/custom/chorus/images/global/info-icon-filled.svg'
      }
      return '/themes/custom/chorus/images/global/info-icon-outline.svg'
    },
    tooltipIsActive(tooltip) {
      return this.activeTooltips.includes(tooltip)
    },
    toggleTooltip(tooltip) {
      if (this.activeTooltips.includes(tooltip)) {
        const index = this.activeTooltips.indexOf(tooltip)
        this.activeTooltips.splice(index, 1)
      } else {
        this.activeTooltips.push(tooltip)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
#roadmapComponent {
  margin: 43px 0 70px;

  .roadmap-header {
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #A1A1A1;
    padding-bottom: 30px;

    @media only screen and (min-width: 992px) {
      padding-bottom: 0;
    }

    .year-toggles {
      display: flex;

      img {
        cursor: not-allowed;
        opacity: .5;

        &.enabled {
          cursor: pointer;
          opacity: 1;
        }
      }

      span {
        color: #313131;
        font-weight: 500;
        font-size: 24px;
      }
    }
  }

  h2 {
    font-family: MuseoSans, serif;
    font-weight: 500;
    font-size: 28px;
    line-height: 34px;

    span {
      padding-left: 20px;
      font-weight: 500;
      font-size: 16px;
    }
  }

  .legend ul {
    margin: 0;
    padding: 0;
    list-style: none;

    li {
      display: flex;
      align-items: center;
      margin-right: 20px;
      font-family: MuseoSans, serif;
      font-weight: 500;
      font-size: 14px;
      line-height: 17px;

      &:last-child {
        margin-right: 0;
      }

      .circle {
        width: 14px;
        height: 14px;
        border-radius: 50%;
        margin-right: 7px;
      }
    }
  }

  .content {
    padding-top: 30px;

    @media only screen and (min-width: 992px) {
      display: flex;
    }

    > div {
      background: #F4F4F4;
      border-radius: 4px;

      &.spacing {
        padding: 10px;
        margin: 0 0 20px 0;
      }

      @media only screen and (min-width: 992px) {
        width: 20%;
        margin: 0 5px !important;
      }

      &:first-child {
        margin-left: 0;
      }

      &:last-child {
        margin-right: 0;
      }

      h3 {
        margin: 0;
        font-size: 16px;
      }

      .tile {
        margin: 10px 0;
        border-radius: 4px;

        &:last-child {
          margin-bottom: 0;
        }

        ul {
          margin: 0;
          padding: 15px 15px 15px 25px;
          background: #FFF;
        }

        h4 {
          margin: 0;
          padding: 7px 11px;
          color: #FFF;
          font-size: 12px;
          line-height: 16px;
          font-weight: 700;
          border-top-left-radius: 4px;
          border-top-right-radius: 4px;

          &.no-points {
            border-radius: 4px;
          }
        }

        li {
          color: #313131;
          font-size: 12px;
          line-height: 16px;
        }

        .pretext {
          margin: 0;
          color: #FFF;
          font-size: 10px;
        }

        .ont-subheading {
          background: #FFF;
          padding: 10px 10px 0 10px;

          p {
            display: inline-block;
            margin: 0;
            padding: 5px;
            background: #F4F4F4;
            color: #313131;
            font-size: 14px;
            line-height: 17px;
          }
        }
      }
    }
  }

  .roadmap-tooltip {
    position: relative;

    span {
      display: none;
      width: 176px;
      padding: 15px 15px 10px 10px;
      position: absolute;
      z-index: 100;
      bottom: 20px;
      left: 20px;
      border: 1px solid #A7336F;
      border-radius: 4px;
      font-size: 12px;
      line-height: 16px;
      background: #FFF;

      &.open {
        display: block;
      }

      .close-tooltip {
        position: absolute;
        top: 5px;
        right: 5px;
      }
    }

    img {
      min-width: 15px;
    }
  }

  .mobile-calendar-image {
    &.open {
      width: 100%;
      height: 100%;
      position: fixed;
      top: 0;
      left: 0;
      z-index: 1;
      overflow-y: scroll;
      background-color: rgba(0, 0, 0, .8);

      h2 {
        display: none;
      }

      img {
        max-width: none;
      }
    }

    .close-mobile-calendar {
      width: 30px;
    }
  }

  .calendar {
    .year-label {
      display: flex;
      justify-content: center;
      align-items: center;
      color: #1E4474;
      font-style: normal;
      font-weight: 700;
      font-size: 14px;
      line-height: 17px;
    }

    .first-col {
      min-width: 93px;
      width: 93px;

      &.wider {
        min-width: 170px;
        width: 170px;
        font-size: 11px;
        font-weight: 500;
      }
    }

    .month-label {
      display: flex;
      flex-grow: 1;
      justify-content: center;
      align-items: center;
      max-width: 80px;
      min-height: 28px;
      font-size: 12px;
      line-height: 14px;
      font-weight: 500;
      color: #313131;
      padding: 0 18px;
      margin: 0 2px;
      background-color: #F4F4F4;
    }

    .calendar-group-name {
      display: flex;
      text-align: center;
      align-items: center;
      justify-content: center;
      padding: 5px;
      word-break: break-word;
      background-color: #F4F4F4;
      color: #313131;
      font-weight: 500;
    }

    .calendar-tile-wrapper {
      width: 100%;
      display: flex;
      flex-direction: column;
      justify-content: space-between;

      > div {
        position: relative;
        height: 45px;
      }

      .calendar-tile {
        padding: 0 8px;
        height: 45px;
        display: flex;
        align-items: center;
        position: absolute;

        &.has-tooltip {
          cursor: pointer;

          p.tile-tooltip {
            display: none;
            justify-content: center;
            align-items: center;
            position: absolute;
            left: 0;
            top: -100%;
            background: #FFF;
            color: #000;
            margin: 0;
            padding: 10px;
            text-align: center;
            min-width: 100px;
            border: 1px solid black;
            border-radius: 5px;
            z-index: 20;
            box-shadow: 0 2px 7px rgba(0, 0, 0, .5);
          }

          &:hover {
            p.tile-tooltip {
              display: flex;
            }
          }
        }

        p {
          color: #313131;
          font-size: 12px;
          line-height: 13px;
          font-weight: 700;
          padding-right: 15px;

          span.blurb {
            font-weight: 300;
          }
        }

        span.arrow {
          width: 0;
          height: 0;
          border-top: 23px solid transparent;
          border-bottom: 23px solid transparent;
          border-left: 22px solid;
          position: absolute;
          right: -1px;
          background: #FFF;
        }
      }
    }
  }

  .custom-style {
    .calendar .roadmap-header span {
      font-size: 12px;
      line-height: 14.4px;
      font-weight: 300;
    }

    .calendar-group-name {
      margin-bottom: 0;
      border-bottom: 1px solid #E1DFDF;
      border-left: 6px solid;
      line-height: 20px;
      justify-content: space-between;

      &.orange {
        border-left-color: #CD4B18;
        background-color: #FAEDE8;
      }

      &.yellow {
        border-left-color: #ECBC57;
        background-color: #FDF8EE;
      }

      &.purple {
        border-left-color: #A7336F;
        background-color: #F5EBF1;
      }
    }

    .calendar-tile-wrapper {
      border-bottom: 1px solid #E1DFDF;

      div.calendar-tile {
        border: 0 !important;
        border-radius: 4px;
        color: #313131 !important;

        p {
          font-size: 14px;
          font-weight: 300;
        }

        span.arrow {
          border-top: 10px solid transparent;
          border-bottom: 10px solid transparent;
          border-left: 13px solid;
          background: transparent;
          right: 4px;

          @media only screen and (min-width: 1200px) {
            right: 10px;
          }
        }

        &.outline-style {
          background-color: #FFF !important;
          border: 1px solid #ECBC57 !important;
          border-left-width: 4px !important;

          span.arrow {
            border-left-color: #ECBC57;
          }
        }
      }

      &.orange {
        .calendar-tile {
          background-color: #FAEDE8 !important;

          span.arrow {
            border-left-color: #CD4B18;
          }
        }
      }

      &.yellow {
        .calendar-tile {
          background-color: #FDF8EE !important;

          span.arrow {
            border-left-color: #ECBC57;
          }
        }
      }

      &.purple {
        .calendar-tile {
          background-color: #F5EBF1 !important;

          span.arrow {
            border-left-color: #A7336F;
          }
        }
      }
    }
  }
}
</style>
